import { GoogleOAuthClient } from './google-oauth-client.js';

const GOOGLE_CALENDAR_ENDPOINT = 'https://www.googleapis.com/calendar/v3';

/**
 * Read-only Google Calendar service guarded by feature flags.
 * Responsible for fetching upcoming events and normalizing them
 * for capacity planning and countdown modules.
 */
export class GoogleCalendarService {
  constructor(options = {}) {
    this.oauthClient = options.oauthClient || null;
    this.featureFlags = options.featureFlags || null;
    this.primaryCalendarId = options.primaryCalendarId || 'primary';
    this.maxResults = options.maxResults || 16;
    this.timezone =
      options.timezone ||
      (typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC');
    this.lastSync = null;
    this.lastError = null;
  }

  isEnabled() {
    return Boolean(this.featureFlags?.isEnabled('googleCalendarRead'));
  }

  async fetchUpcomingEvents({ timeMin = null, timeMax = null } = {}) {
    if (!this.isEnabled()) {
      return { status: 'disabled', events: [] };
    }

    if (!this.oauthClient) {
      throw new Error('[GoogleCalendarService] OAuth client not configured.');
    }

    const accessToken = await this.oauthClient.getAccessToken();
    if (!accessToken) {
      return { status: 'unauthenticated', events: [] };
    }

    const now = new Date();
    const query = new URLSearchParams({
      maxResults: String(this.maxResults),
      singleEvents: 'true',
      orderBy: 'startTime',
      timeMin: (timeMin || now).toISOString(),
      timeZone: this.timezone,
    });

    if (timeMax) {
      query.set('timeMax', timeMax.toISOString());
    }

    const url = `${GOOGLE_CALENDAR_ENDPOINT}/calendars/${encodeURIComponent(this.primaryCalendarId)}/events?${query.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.lastError = {
          at: new Date().toISOString(),
          message: errorText,
          status: response.status,
        };
        return { status: 'error', events: [], error: this.lastError };
      }

      const payload = await response.json();
      const events = Array.isArray(payload.items)
        ? payload.items.map((item) => this.normalizeEvent(item))
        : [];

      this.lastSync = new Date().toISOString();
      this.lastError = null;

      return {
        status: 'ok',
        events,
        raw: payload,
        syncedAt: this.lastSync,
      };
    } catch (error) {
      this.lastError = {
        at: new Date().toISOString(),
        message: error?.message || 'Network error',
        status: 'network_error',
      };
      return { status: 'network_error', events: [], error: this.lastError };
    }
  }

  hasWriteAccess() {
    return Boolean(this.oauthClient?.hasScopes?.(GoogleCalendarService.WRITE_SCOPES));
  }

  normalizeEvent(event) {
    const start = this.parseEventDate(event.start);
    const end = this.parseEventDate(event.end);
    return {
      id: event.id,
      calendarId: event.organizer?.email || this.primaryCalendarId,
      summary: event.summary || '(No title)',
      description: event.description || '',
      location: event.location || '',
      start,
      end,
      isAllDay: Boolean(event.start?.date),
      hangoutLink: event.hangoutLink || null,
      attendees: this.extractAttendees(event.attendees),
      reminders: event.reminders || null,
      raw: event,
    };
  }

  parseEventDate(dateInfo) {
    if (!dateInfo) {
      return null;
    }
    if (dateInfo.dateTime) {
      return new Date(dateInfo.dateTime);
    }
    if (dateInfo.date) {
      return new Date(`${dateInfo.date}T00:00:00`);
    }
    return null;
  }

  extractAttendees(attendees) {
    if (!Array.isArray(attendees)) return [];
    return attendees.map((attendee) => ({
      email: attendee.email,
      displayName: attendee.displayName || attendee.email,
      responseStatus: attendee.responseStatus,
      self: attendee.self || false,
    }));
  }

  /**
   * Compare planned tasks against calendar events to flag conflicts.
   * @param {Array} events - Normalized calendar events.
   * @param {Array} sessions - Planned work sessions with start/end ISO strings.
   */
  detectConflicts(events, sessions) {
    if (!Array.isArray(events) || !Array.isArray(sessions)) {
      return [];
    }

    return sessions
      .map((session) => {
        const sessionStart = new Date(session.start);
        const sessionEnd = new Date(session.end);
        if (Number.isNaN(sessionStart.valueOf()) || Number.isNaN(sessionEnd.valueOf())) {
          return null;
        }

        const overlaps = events.filter((event) => {
          if (!event.start || !event.end) return false;
          return event.start < sessionEnd && event.end > sessionStart;
        });

        if (overlaps.length === 0) {
          return null;
        }

        return {
          session,
          conflicts: overlaps,
        };
      })
      .filter(Boolean);
  }

  normalizeEventTime(input) {
    if (!input) {
      return null;
    }
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return {
      dateTime: date.toISOString(),
      timeZone: this.timezone,
    };
  }

  async createEvent({ summary, description = '', start, end, reminders = { useDefault: true }, calendarId = this.primaryCalendarId }) {
    if (!this.oauthClient) {
      throw new Error('[GoogleCalendarService] OAuth client not configured.');
    }

    if (!this.hasWriteAccess()) {
      throw new Error('[GoogleCalendarService] Calendar write scope not granted.');
    }

    const accessToken = await this.oauthClient.getAccessToken();
    if (!accessToken) {
      throw new Error('[GoogleCalendarService] Unable to create event without an access token.');
    }

    const normalizedStart = this.normalizeEventTime(start);
    const normalizedEnd = this.normalizeEventTime(end);
    if (!normalizedStart || !normalizedEnd) {
      throw new Error('[GoogleCalendarService] Invalid event start or end time.');
    }

    const payload = {
      summary: summary || 'Life Dashboard block',
      description,
      start: normalizedStart,
      end: normalizedEnd,
      reminders,
    };

    const response = await fetch(
      `${GOOGLE_CALENDAR_ENDPOINT}/calendars/${encodeURIComponent(calendarId)}/events`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`[GoogleCalendarService] Failed to create event: ${message}`);
    }

    const created = await response.json();
    this.lastError = null;
    return this.normalizeEvent(created);
  }
}

GoogleCalendarService.ENDPOINT = GOOGLE_CALENDAR_ENDPOINT;
GoogleCalendarService.DEFAULT_SCOPES = ['https://www.googleapis.com/auth/calendar.readonly'];
GoogleCalendarService.WRITE_SCOPES = ['https://www.googleapis.com/auth/calendar.events'];
GoogleCalendarService.GoogleOAuthClient = GoogleOAuthClient;
